import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import AcademyExam from "@/components/AcademyExam";
import AcademySuggest from "@/components/AcademySuggest";
import { academyApi } from "@/lib/academy";

function speak(text) {
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.95;
    window.speechSynthesis.speak(u);
  } catch (e) { /* device-dependent; silently unavailable */ }
}

/** Minimal markdown renderer for lesson material: ##/### headings, lists, links, paragraphs. */
function renderMarkdown(md) {
  const out = [];
  let list = [];
  const flush = () => { if (list.length) { out.push(<ul key={`ul${out.length}`}>{list.map((li, i) => <li key={i}>{inline(li)}</li>)}</ul>); list = []; } };
  const inline = (s) => {
    const parts = s.split(/(\[[^\]]+\]\([^)]+\))/g);
    return parts.map((p, i) => {
      const m = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      return m ? <a key={i} href={m[2]} target="_blank" rel="noopener noreferrer">{m[1]}</a> : p;
    });
  };
  for (const raw of (md || "").split("\n")) {
    const line = raw.trim();
    if (!line) { flush(); continue; }
    if (line.startsWith("### ")) { flush(); out.push(<h4 key={out.length}>{inline(line.slice(4))}</h4>); }
    else if (line.startsWith("## ")) { flush(); out.push(<h3 key={out.length}>{inline(line.slice(3))}</h3>); }
    else if (line.startsWith("# ")) { flush(); out.push(<h3 key={out.length}>{inline(line.slice(2))}</h3>); }
    else if (line.startsWith("- ")) { list.push(line.slice(2)); }
    else { flush(); out.push(<p key={out.length}>{inline(line)}</p>); }
  }
  flush();
  return out;
}

export default function AcademyLesson() {
  const { id } = useParams();
  const [lesson, setLesson] = useState(null);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [practiceIdx, setPracticeIdx] = useState({});
  const [written, setWritten] = useState("");
  const [writtenMsg, setWrittenMsg] = useState("");
  const [showExam, setShowExam] = useState(false);
  const [passed, setPassed] = useState(false);

  useEffect(() => {
    setNeedsAuth(false);
    setPassed(false);
    academyApi.lesson(id).then((d) => { setLesson(d); setPassed(d.progress?.some((p) => p.kind === "section" && p.passed && p.sectionId === "assessment")); }).catch((e) => {
      if (e.response?.status === 401) setNeedsAuth(true);
      else setLesson({ blocked: "Lesson unavailable right now — please try again." });
    });
    return () => { try { window.speechSynthesis?.cancel(); } catch (e) {} };
  }, [id]);

  if (needsAuth) {
    return (
      <>
        <Seo title={`Lesson ${id} | Solar Black Belt Academy`} />
        <section className="wrap" style={{ padding: "80px 20px", textAlign: "center" }} data-testid="academy-lesson-signin">
          <span className="eyebrow">STUDENTS ONLY</span>
          <h1>Sign in to open this lesson.</h1>
          <p className="lead" style={{ maxWidth: 560, margin: "0 auto 18px" }}>
            The class material lives inside the academy. Sign in — or enroll free in under a minute — and this
            lesson opens with your progress saved.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <Link className="btn lime" data-testid="academy-lesson-signin-btn" to="/academy/login" state={{ from: `/academy/lessons/${id}` }}>Sign in to continue ↗</Link>
            <Link className="btn light" to="/academy/register">Enroll free</Link>
          </div>
        </section>
      </>
    );
  }
  if (!lesson) return <section className="wrap" style={{ padding: 80 }}><p>Opening the lesson…</p></section>;
  if (lesson.blocked) {
    return (
      <>
        <Seo title={`Lesson ${id} | Solar Black Belt Academy`} />
        <section className="wrap" style={{ padding: "80px 20px", textAlign: "center" }} data-testid="academy-lesson-locked">
          <span className="eyebrow">{lesson.locked ? "LOCKED" : "NOT PUBLISHED"}</span>
          <h1>{lesson.title || `Lesson ${id}`}</h1>
          <p className="lead" style={{ maxWidth: 560, margin: "0 auto 18px" }}>{lesson.blocked}</p>
          <Link className="btn lime" to="/academy/dashboard">Back to my dashboard →</Link>
        </section>
      </>
    );
  }

  const plain = [lesson.title, ...lesson.blocks.map((b) => `${b.heading}. ${b.text}`), lesson.exercise || ""].join(" ");

  async function answerPractice(q, choiceId) {
    const res = await academyApi.practice(id, q.id, choiceId);
    setPracticeIdx({ ...practiceIdx, [q.id]: { choiceId, ...res } });
  }

  return (
    <>
      <Seo title={`${lesson.title} | ${lesson.belt} Belt — Solar Black Belt Academy`} description={lesson.objectives.join(" ")} />
      <section className="page-hero"><div className="wrap">
        <span className="eyebrow">{lesson.belt.toUpperCase()} BELT · LESSON {id} · ≈{lesson.estimatedMinutes} MIN</span>
        <h1 data-testid="academy-lesson-title">{lesson.title}</h1>
        <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
          <button className="btn small" data-testid="academy-readaloud" onClick={() => speak(plain)}>▶ Read aloud (device voice)</button>
          <button className="btn small" onClick={() => { try { window.speechSynthesis.cancel(); } catch (e) {} }}>■ Stop</button>
        </div>
        <p className="micro" style={{ marginTop: 8 }}>Read-aloud uses your device's speech synthesis — availability varies by device.</p>
      </div></section>

      <section className="wrap academy-section" style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 32 }}>
        <div>
          <h2>Objectives</h2>
          <ul className="lead" style={{ fontSize: 16 }}>{lesson.objectives.map((o) => <li key={o}>{o}</li>)}</ul>
          {lesson.blocks.map((b) => (
            <div key={b.heading} style={{ marginTop: 26 }}>
              <h3>{b.heading}</h3>
              <p>{b.text}</p>
            </div>
          ))}
          {lesson.markdown && (
            <div className="academy-deep" style={{ marginTop: 30 }} data-testid="academy-deep">
              {renderMarkdown(lesson.markdown)}
              {lesson.enriched && (
                <p className="micro" style={{ marginTop: 14, opacity: 0.8 }}>
                  Enriched with AI assistance and awaiting reviewer polish — spot an error? Send it through the suggestion box below.
                </p>
              )}
            </div>
          )}
          {lesson.exercise && (
            <div style={{ marginTop: 26 }}>
              <h3>Written exercise — human graded</h3>
              <p>{lesson.exercise}</p>
              <textarea rows={4} maxLength={8000} value={written} onChange={(e) => setWritten(e.target.value)} style={{ width: "100%", maxWidth: 640 }} data-testid="academy-written-input" placeholder="Write your answer (40+ characters)…" />
              <div style={{ display: "flex", gap: 10, marginTop: 10, alignItems: "center" }}>
                <button className="btn small lime" data-testid="academy-written-submit" onClick={async () => { try { await academyApi.submitWritten(id, written); setWrittenMsg("Submitted — a reviewer grades written work (pass mark 8/10)."); } catch (e) { setWrittenMsg(e.response?.data?.detail || "Failed."); } }}>Submit exercise</button>
                {writtenMsg && <span className="micro">{writtenMsg}</span>}
              </div>
            </div>
          )}
        </div>
        <aside>
          {lesson.illustration && lesson.illustration.path?.includes("belt-scenes") && (
            <img src={`/academy-assets/belt-${lesson.belt.toLowerCase()}.jpg`} alt={lesson.illustration.alt} style={{ width: "100%", borderRadius: 14 }} loading="lazy" />
          )}
          {lesson.illustration?.path?.includes("energy-team") && (
            <img src="/academy-assets/energy-team.png" alt={lesson.illustration.alt} style={{ width: "100%", borderRadius: 14 }} loading="lazy" />
          )}
          <h3 style={{ marginTop: 18 }}>Vocabulary</h3>
          {lesson.vocabulary.map((v) => (
            <p key={v.id} className="micro" style={{ marginBottom: 10 }}><strong>{v.term}</strong> — {v.definition}</p>
          ))}
          <h3>Sources</h3>
          {lesson.sources.map((s) => (
            <p key={s.id} className="micro"><a href={s.url} target="_blank" rel="noopener noreferrer">{new URL(s.url).hostname.replace("www.", "")} ↗</a>{s.reviewed_on ? ` — source check ${s.reviewed_on}` : ""}</p>
          ))}
        </aside>
      </section>

      <section className="wrap academy-section" data-testid="academy-practice">
        <span className="eyebrow">PRACTICE — INSTANT FEEDBACK, SEPARATE FROM EXAMS</span>
        <h2>Test yourself</h2>
        {lesson.practice.map((q) => {
          const st = practiceIdx[q.id];
          return (
            <div key={q.id} className="academy-practice-item">
              <p><strong>{q.stem}</strong></p>
              {q.choices.map((c) => (
                <label key={c.id} className="academy-choice" style={{ borderColor: st?.choiceId === c.id ? "var(--teal)" : "var(--line)" }}>
                  <input type="radio" name={`p-${q.id}`} disabled={!!st} checked={st?.choiceId === c.id} onChange={() => answerPractice(q, c.id)} />
                  {c.text}
                </label>
              ))}
              {st && <p className="micro" style={{ color: st.correct ? "var(--teal)" : "#a33" }}>{st.correct ? "✓ Correct." : "✗ Not this one."} {st.rationale?.slice(0, 220)}</p>}
            </div>
          );
        })}
      </section>

      <section className="wrap academy-section" data-testid="academy-lesson-exam">
        <span className="eyebrow">SECTION-END GRADED ASSESSMENT</span>
        <h2>Finish the lesson</h2>
        <p className="micro">Three knowledge questions plus vocabulary from this lesson — graded server-side. Pass to unlock the next lesson.</p>
        {passed ? (
          <div className="academy-exam" style={{ borderColor: "var(--teal)" }} data-testid="academy-lesson-next">
            <h3>✓ Lesson passed</h3>
            {lesson.next ? (
              <>
                <p>Next up: <strong>{lesson.next.title}</strong> ({lesson.next.belt} Belt)</p>
                <Link className="btn lime" data-testid="academy-next-lesson-btn" to={`/academy/lessons/${lesson.next.id}`}>Continue to {lesson.next.id} →</Link>
              </>
            ) : (
              <p>That was the final lesson — the belt exam is waiting on your <Link to={`/academy/belts/${lesson.belt.toLowerCase()}`}>{lesson.belt} Belt page</Link>.</p>
            )}
          </div>
        ) : showExam ? (
          <AcademyExam scope={{ kind: "lesson", id }} onPassed={() => setPassed(true)} onClose={() => setShowExam(false)} />
        ) : (
          <button className="btn lime" data-testid="academy-lesson-exam-open" onClick={() => setShowExam(true)}>Start the graded assessment</button>
        )}
        <AcademySuggest page={`/academy/lessons/${id}`} />
      </section>
    </>
  );
}
